import { useEffect, useState } from "react";
import "./tokens.css";

import { LangContext } from "./LangContext";
import useDesertEye from "../hooks/useDesertEye";
import useAnchor from "../hooks/useAnchor";

import PCHeader from "./PCHeader";
import PCNewsTab from "./PCNewsTab";
import PCUnverifiedTab from "./PCUnverifiedTab";
import PCDeeperTab from "./PCDeeperTab";
import PCIdeaTab from "./PCIdeaTab";
import PCAnchorTab from "./PCAnchorTab";
import PCSearch from "./PCSearch";

export default function PCApp() {
  const {
    news, loading, error,
    byTier, socmint, adversary, adversaryFrame, themes,
    deepLoading, deepError, ach, cuiBono, scenarios, topArticles,
    subScenariosMap, subLoadingIdx, handleLoadSubScenarios,
    autoIdeas, autoIdeaL, ideaIn, ideas, ideaL, ideaErr,
    setIdeaIn, genIdeas, genMoreIdeas,
    handleOverride, load,
    unverifiedPanel, setUnverifiedPanel,
  } = useDesertEye();

  const anchor = useAnchor({ news, scenarios });

  const [active, setActive] = useState("news");
  const [selected, setSelected] = useState(null);
  const [lang, setLang] = useState("ko");
  const [filters, setFilters] = useState({
    tier: new Set(),
    dir:  new Set(),
    sort: "priority",
  });
  const [searchOpen, setSearchOpen] = useState(false);

  // ⌘K / Ctrl+K — search shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      } else if (e.key === "Escape" && searchOpen) {
        setSearchOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  const handleSearchSelect = (article) => {
    setSelected(article);
    setActive("news");
    setSearchOpen(false);
  };

  return (
    <LangContext.Provider value={lang}>
    <div className="pc-root">
      <PCHeader active={active} onTab={setActive} onSearch={() => setSearchOpen(true)} lang={lang} setLang={setLang} />

      <main style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>
        {active === "news" && (
          <PCNewsTab
            news={news}
            themes={themes}
            byTier={byTier}
            socmint={socmint}
            adversary={adversary}
            selected={selected}
            onSelect={setSelected}
            filters={filters}
            setFilters={setFilters}
            onOverride={handleOverride}
            onLoad={load}
          />
        )}

        {active === "unverified" && (
          <PCUnverifiedTab
            socmint={socmint}
            adversary={adversary}
            adversaryFrame={adversaryFrame}
            unverifiedPanel={unverifiedPanel}
            setUnverifiedPanel={setUnverifiedPanel}
            onOverride={handleOverride}
          />
        )}

        {active === "deeper" && (
          <PCDeeperTab
            deepLoading={deepLoading}
            deepError={deepError}
            ach={ach}
            cuiBono={cuiBono}
            scenarios={scenarios}
            topArticles={topArticles}
            subScenariosMap={subScenariosMap}
            subLoadingIdx={subLoadingIdx}
            onLoadSubScenarios={handleLoadSubScenarios}
          />
        )}

        {active === "idea" && (
          <PCIdeaTab
            ideaIn={ideaIn}
            ideas={ideas}
            autoIdeas={autoIdeas}
            ideaL={ideaL}
            autoIdeaL={autoIdeaL}
            ideaErr={ideaErr}
            onIdeaInputChange={setIdeaIn}
            onGenIdeas={genIdeas}
            onMore={genMoreIdeas}
          />
        )}

        {active === "anchor" && (
          <PCAnchorTab
            directionKeyword={anchor.directionKeyword}
            onDirectionChange={anchor.setDirectionKeyword}
            briefing={anchor.briefing}
            briefingTs={anchor.briefingTs}
            collectedSources={anchor.collectedSources}
            generating={anchor.generating}
            error={anchor.error}
            sourceStatuses={anchor.sourceStatuses}
            phase={anchor.phase}
            onGenerate={anchor.generate}
          />
        )}
      </main>

      {searchOpen && (
        <PCSearch
          articles={news}
          adversary={adversary}
          socmint={socmint}
          onClose={() => setSearchOpen(false)}
          onSelect={handleSearchSelect}
        />
      )}

      {/* Loading/error banners */}
      {loading && news.length === 0 && (
        <div style={{
          position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)",
          padding: "8px 14px", background: "var(--pc-bg-2)", border: "1px solid var(--pc-line-3)",
          fontSize: 11, color: "var(--pc-fg-3)", letterSpacing: ".08em",
        }}>
          뉴스 수집 중…
        </div>
      )}
      {error && (
        <div style={{
          position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)",
          padding: "8px 14px", background: "var(--pc-bg-2)", border: "1px solid var(--pc-dir-up)",
          fontSize: 11, color: "var(--pc-dir-up)", letterSpacing: ".05em",
        }}>
          수집 실패 — {error}
        </div>
      )}
    </div>
    </LangContext.Provider>
  );
}
